import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Printer, Flame, Target, Lightbulb, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import PracticeScenario from '../shared/PracticeScenario';
import PageMessaging from '../shared/PageMessaging';

export default function RiskDreadedExperience() {
  const navigate = useNavigate();
  const printPage = () => { window.print(); toast.success('Print dialog opened'); };

  const dreadedExperiences = [
    { experience: 'Rejection', avoidance: 'Never asking for what we want', cost: 'Unmet needs, resentment' },
    { experience: 'Conflict', avoidance: 'People-pleasing, silence', cost: 'Inauthenticity, disconnection' },
    { experience: 'Failure', avoidance: 'Not trying, staying small', cost: 'Unfulfilled potential, regret' },
    { experience: 'Vulnerability', avoidance: 'Armor, walls, independence', cost: 'Loneliness, shallow relationships' },
    { experience: 'Disappointment', avoidance: 'Not hoping, cynicism', cost: 'Joylessness, numbness' }
  ];

  const steps = [
    {
      number: 1,
      title: 'Name the Dreaded Experience',
      description: 'What are you most afraid of feeling? Be specific. "I\'m afraid of feeling rejected/abandoned/inadequate."'
    },
    {
      number: 2,
      title: 'Recognize the Pattern',
      description: 'How has avoiding this experience shaped your life? What opportunities have you missed? What strategies have you used?'
    },
    {
      number: 3,
      title: 'Connect to the Underlying Need',
      description: 'What universal need is the fear trying to protect? Belonging? Safety? Worth? Competence?'
    },
    {
      number: 4,
      title: 'Start Small',
      description: 'Find a low-stakes situation to practice risking the dreaded experience. Build your tolerance gradually.'
    },
    {
      number: 5,
      title: 'Experience Survival',
      description: 'Notice: You survived. You didn\'t die. The dreaded experience, while uncomfortable, was bearable. This is rewiring.'
    },
    {
      number: 6,
      title: 'Celebrate Courage',
      description: 'Acknowledge yourself for taking the risk. This builds a new internal narrative: "I can handle hard feelings."'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{`@media print { body { background: white !important; } .no-print { display: none !important; } .print-page { background: white !important; color: black !important; } }`}</style>
      
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 no-print">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Back to Workbook
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Risk the Dreaded Experience!</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white">
                <Printer className="h-4 w-4 mr-2" />Print
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <BookOpen className="h-4 w-4" />
                <span>Page 59</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12 print-page">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <Flame className="h-16 w-16 text-orange-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">Risk the Dreaded Experience!</h2>
            <p className="text-slate-300 max-w-2xl mx-auto">What we resist persists. What we risk transforms. Learning to face our deepest fears opens new possibilities.</p>
          </div>

          <div className="bg-gradient-to-r from-orange-900/30 to-red-900/30 border border-orange-700/30 rounded-lg p-6 mb-10">
            <h3 className="text-xl font-bold text-orange-200 mb-4 flex items-center gap-2">
              <Target className="h-5 w-5" /> The Core Insight
            </h3>
            <p className="text-orange-100 text-lg mb-4">Much of our behavior is organized around avoiding certain experiences we learned were intolerable.</p>
            <p className="text-slate-300">But here\'s the secret: <strong className="text-white">The dreaded experience is usually more bearable than the life we create by avoiding it.</strong></p>
          </div>

          <h3 className="text-2xl font-bold text-white mb-6">Common Dreaded Experiences and Their Costs</h3>
          <div className="overflow-x-auto mb-10">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-3 px-4 text-amber-400 font-bold">Dreaded Experience</th>
                  <th className="text-left py-3 px-4 text-amber-400 font-bold">Avoidance Strategy</th>
                  <th className="text-left py-3 px-4 text-amber-400 font-bold">Hidden Cost</th>
                </tr>
              </thead>
              <tbody>
                {dreadedExperiences.map((item, index) => (
                  <tr key={index} className="border-b border-slate-700/50">
                    <td className="py-3 px-4 text-white font-medium">{item.experience}</td>
                    <td className="py-3 px-4 text-slate-300">{item.avoidance}</td>
                    <td className="py-3 px-4 text-red-300">{item.cost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-green-900/20 border border-green-700/30 rounded-lg p-6 mb-10">
            <h3 className="text-xl font-bold text-green-200 mb-4 flex items-center gap-2">
              <TrendingUp className="h-5 w-5" /> Steps to Risk the Dreaded Experience
            </h3>
            <div className="space-y-4">
              {steps.map((step, index) => (
                <div key={index} className="flex gap-4 items-start">
                  <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">{step.number}</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-green-200">{step.title}</h4>
                    <p className="text-slate-300 text-sm">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-6 mb-10">
            <h3 className="text-lg font-bold text-purple-200 mb-3 flex items-center gap-2">
              <Lightbulb className="h-5 w-5" /> A Powerful Question to Ask
            </h3>
            <div className="bg-slate-900/50 rounded-lg p-4">
              <p className="text-white text-lg italic">"What would I do differently if I weren\'t afraid of [the dreaded experience]?"</p>
            </div>
            <p className="text-purple-100 mt-4">Let this question guide you toward a more alive, authentic life. The freedom on the other side of fear is worth the temporary discomfort.</p>
          </div>

          <PracticeScenario pageTitle="Risk the Dreaded Experience" pageNumber={59} />
          <PageMessaging pageTitle="Risk the Dreaded Experience" pageNumber={59} />
        </div>
      </div>
    </div>
  );
}
